import React, { useState } from 'react';
import {
    GenericTable,
    GenericTableColumn,
} from '@ui-components/Table/GenericTable';
import {
    TopologyData,
    TopologyNode,
    TopologyLink,
    BaselineData,
    usToMs,
    getLinkStrokeColor,
    getBaselineDeviation,
    getRawBaselineLink,
    isTopologyLinkOfflineOrDropped,
    resolveLinkBandwidth,
} from '../TopologyData';

export type TableSubTab = 'nodes' | 'links';

interface Props {
    data: TopologyData;
    baseline: BaselineData | null;
    rawBaseline?: BaselineData | null;
    /** Synthetic links whose IS-IS adjacency dropped entirely (present in
     *  baseline, absent from the snapshot). Listed with the live links. */
    downLinks?: TopologyLink[];
    onNodeClick?: (nodeId: string) => void;
    onLinkClick?: (linkId: string) => void;
    subTab?: TableSubTab;
}

interface LinkRow extends TopologyLink {
    pingUs: number;
    baselineDeviation: string | null;
    baselineDeltaUs: number | null;
    costPerMs: number | null;
}

const TabBtn: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => (
    <button
        onClick={onClick}
        className={`px-4 py-1.5 text-xs font-semibold rounded-full
            transition-colors cursor-pointer ${
                active
                    ? 'bg-semantic-bg-brand-bold-secondary text-white shadow-sm'
                    : 'bg-semantic-bg-primary text-semantic-text-secondary border border-semantic-border-primary hover:bg-semantic-state-active'
            }`}
    >
        {children}
    </button>
);

const TopologyTableView: React.FC<Props> = ({
    data,
    baseline,
    rawBaseline = null,
    downLinks = [],
    onNodeClick,
    onLinkClick,
    subTab: controlledSubTab,
}) => {
    const [internalSubTab, setSubTab] = useState<TableSubTab>('nodes');
    const subTab = controlledSubTab ?? internalSubTab;
    const baselineLinks = baseline?.links ?? {};

    const linkStrokeColor = (row: TopologyLink) =>
        getLinkStrokeColor(
            row,
            baselineLinks[row.id]?.avgDelayUs,
            getRawBaselineLink(rawBaseline, row.id)
        );

    // --- Node columns ------------------------------------------------
    const nodeColumns: GenericTableColumn<TopologyNode>[] = [
        {
            header: 'Hostname',
            accessor: 'id',
            fixed: 'left',
            width: '200px',
            sortable: true,
        },
        {
            header: 'Datacenter',
            width: '130px',
            sortable: true,
            sortAccessor: (row) =>
                row.location.datacenterId || row.site,
            renderCell: (row) => (
                <span title={row.site}>
                    {row.location.datacenterId || row.site}
                </span>
            ),
        },
        {
            header: 'Role',
            accessor: 'role',
            sortable: true,
            width: '80px',
        },
        {
            header: 'Type',
            width: '110px',
            sortable: true,
            sortAccessor: (row) => row.type,
            renderCell: (row) => (
                <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        row.type === 'route-reflector'
                            ? 'bg-semantic-bg-danger-primary text-semantic-text-danger'
                            : 'bg-semantic-bg-brand-primary text-semantic-text-brand'
                    }`}
                >
                    {row.type === 'route-reflector' ? 'Reflector' : 'Router'}
                </span>
            ),
        },
        {
            header: 'Local',
            width: '70px',
            renderCell: (row) =>
                row.isLocal ? (
                    <span
                        className="text-[10px] font-bold px-1.5 py-0.5
                            rounded-full uppercase tracking-wide
                            bg-semantic-bg-danger-bold text-semantic-text-inverse-primary"
                    >
                        Local
                    </span>
                ) : null,
        },
        {
            header: 'Location',
            sortable: true,
            sortAccessor: (row) => row.location.city,
            renderCell: (row) =>
                `${row.location.city}, ${row.location.country}`,
        },
        {
            header: 'Flex Algos',
            width: '100px',
            sortable: true,
            sortAccessor: (row) => row.flexAlgoCount,
            renderCell: (row) => (
                <span className="font-mono text-sm">
                    {row.flexAlgoCount}
                </span>
            ),
        },
        {
            header: 'SRv6 Locators',
            width: '120px',
            sortable: true,
            sortAccessor: (row) => row.srv6Locators.length,
            renderCell: (row) => (
                <span className="font-mono text-sm">
                    {row.srv6Locators.length}
                </span>
            ),
        },
        {
            header: 'Area',
            accessor: 'areaAddress',
            sortable: true,
            width: '110px',
        },
    ];

    // --- Link rows with computed fields ------------------------------
    const linkRows: LinkRow[] = [...data.links, ...downLinks].map((link) => {
        const bl = baselineLinks[link.id];
        const offline = isTopologyLinkOfflineOrDropped(
            link,
            getRawBaselineLink(rawBaseline, link.id)
        );
        const fwd = link.forward?.avgDelayUs;
        const rev = link.reverse?.avgDelayUs;
        const pingUs =
            fwd != null && rev != null ? fwd + rev : link.avgDelayUs * 2;
        const latencyMs = link.avgDelayUs / 1000;
        return {
            ...link,
            pingUs,
            baselineDeviation:
                !offline && bl
                    ? getBaselineDeviation(link.avgDelayUs, bl.avgDelayUs)
                    : null,
            baselineDeltaUs:
                !offline && bl ? link.avgDelayUs - bl.avgDelayUs : null,
            costPerMs:
                !offline && latencyMs > 0 ? link.metric / latencyMs : null,
        };
    });

    // --- Link columns ------------------------------------------------
    const linkColumns: GenericTableColumn<LinkRow>[] = [
        {
            header: 'Source',
            accessor: 'source',
            fixed: 'left',
            width: '180px',
            sortable: true,
            wrapMode: 'truncate',
        },
        {
            header: 'Src Interface',
            width: '150px',
            sortable: true,
            sortAccessor: (row) => row.sourceInterface ?? '',
            renderCell: (row) =>
                row.sourceInterface ? (
                    <span
                        className="font-mono text-xs"
                        title={row.sourceInterfaceDesc ?? undefined}
                    >
                        {row.sourceInterface}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary text-xs">
                        —
                    </span>
                ),
        },
        {
            header: 'Target',
            accessor: 'target',
            width: '180px',
            sortable: true,
            wrapMode: 'truncate',
        },
        {
            header: 'Tgt Interface',
            width: '150px',
            sortable: true,
            sortAccessor: (row) => row.targetInterface ?? '',
            renderCell: (row) =>
                row.targetInterface ? (
                    <span
                        className="font-mono text-xs"
                        title={row.targetInterfaceDesc ?? undefined}
                    >
                        {row.targetInterface}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary text-xs">
                        —
                    </span>
                ),
        },
        {
            header: 'Type',
            width: '100px',
            sortable: true,
            sortAccessor: (row) => (row.isInterSite ? 0 : 1),
            renderCell: (row) => (
                <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        row.isInterSite
                            ? 'bg-semantic-bg-brand-secondary text-semantic-text-brand'
                            : 'bg-semantic-bg-secondary text-semantic-text-secondary'
                    }`}
                >
                    {row.isInterSite ? 'Inter-Site' : 'Intra-Site'}
                </span>
            ),
        },
        {
            header: 'Bandwidth',
            sortable: true,
            sortAccessor: (row) => resolveLinkBandwidth(row).kbps,
            width: '110px',
            renderCell: (row) => resolveLinkBandwidth(row).label,
        },
        {
            header: 'Metric',
            accessor: 'metric',
            sortable: true,
            width: '80px',
            renderCell: (row) => (
                <span className="font-mono text-sm">{row.metric}</span>
            ),
        },
        {
            header: 'Avg Latency',
            width: '110px',
            sortable: true,
            sortAccessor: (row) => row.avgDelayUs,
            renderCell: (row) => {
                const offline = isTopologyLinkOfflineOrDropped(
                    row,
                    getRawBaselineLink(rawBaseline, row.id)
                );
                return (
                    <span
                        className="font-semibold text-sm"
                        style={{ color: linkStrokeColor(row) }}
                    >
                        {offline ? 'Offline' : usToMs(row.avgDelayUs)}
                    </span>
                );
            },
        },
        {
            header: 'RTT (Ping)',
            width: '110px',
            sortable: true,
            sortAccessor: (row) => row.pingUs,
            renderCell: (row) => {
                const offline = isTopologyLinkOfflineOrDropped(
                    row,
                    getRawBaselineLink(rawBaseline, row.id)
                );
                return (
                    <span
                        className="text-sm"
                        style={{ color: linkStrokeColor(row) }}
                    >
                        {offline ? '—' : usToMs(row.pingUs)}
                    </span>
                );
            },
        },
        {
            header: 'Fwd Latency',
            width: '110px',
            sortable: true,
            sortAccessor: (row) => row.forward?.avgDelayUs ?? Infinity,
            renderCell: (row) => (
                <span className="text-sm font-mono">
                    {usToMs(row.forward?.avgDelayUs ?? null)}
                </span>
            ),
        },
        {
            header: 'Rev Latency',
            width: '110px',
            sortable: true,
            sortAccessor: (row) => row.reverse?.avgDelayUs ?? Infinity,
            renderCell: (row) => (
                <span className="text-sm font-mono">
                    {usToMs(row.reverse?.avgDelayUs ?? null)}
                </span>
            ),
        },
        {
            header: 'vs Baseline',
            width: '100px',
            sortable: true,
            sortAccessor: (row) => row.baselineDeltaUs ?? Infinity,
            renderCell: (row) =>
                row.baselineDeviation ? (
                    <span
                        className="text-sm font-medium"
                        style={{ color: linkStrokeColor(row) }}
                    >
                        {row.baselineDeviation}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary text-xs">
                        —
                    </span>
                ),
        },
        {
            header: 'Cost/ms',
            width: '90px',
            sortable: true,
            sortAccessor: (row) => row.costPerMs ?? Infinity,
            renderCell: (row) =>
                row.costPerMs != null ? (
                    <span className="font-mono text-sm">
                        {row.costPerMs.toFixed(1)}
                    </span>
                ) : (
                    <span className="text-semantic-text-secondary text-xs">
                        —
                    </span>
                ),
        },
    ];

    return (
        <div className="flex flex-col gap-3 h-full overflow-auto">
            {!controlledSubTab && (
                <div className="flex gap-1 p-1 bg-semantic-bg-secondary rounded-lg w-fit self-start">
                    <TabBtn
                        active={subTab === 'nodes'}
                        onClick={() => setSubTab('nodes')}
                    >
                        Nodes ({data.nodes.length})
                    </TabBtn>
                    <TabBtn
                        active={subTab === 'links'}
                        onClick={() => setSubTab('links')}
                    >
                        Links ({data.links.length + downLinks.length})
                    </TabBtn>
                </div>
            )}

            {/* Nodes table */}
            {subTab === 'nodes' && (
                <GenericTable
                    columns={nodeColumns}
                    data={data.nodes}
                    rowKey={(row) => row.id}
                    initialRowsPerPage={20}
                    horizontalScroll="always"
                    maxTableBodyHeight="calc(100vh - 320px)"
                    onRowClick={
                        onNodeClick
                            ? (row) => onNodeClick(row.id)
                            : undefined
                    }
                />
            )}

            {/* Links table */}
            {subTab === 'links' && (
                <GenericTable
                    columns={linkColumns}
                    data={linkRows}
                    rowKey={(row) => row.id}
                    initialRowsPerPage={20}
                    horizontalScroll="always"
                    maxTableBodyHeight="calc(100vh - 320px)"
                    onRowClick={
                        onLinkClick
                            ? (row) => onLinkClick(row.id)
                            : undefined
                    }
                />
            )}
        </div>
    );
};

export default TopologyTableView;
